import { intersects } from 'ol/extent';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import ImageWMSSource from 'ol/source/ImageWMS';
import ImageArcGISRestSource from 'ol/source/ImageArcGISRest';
import XYZSource from 'ol/source/XYZ';
import WMTSSource from 'ol/source/WMTS';
import VectorSource from 'ol/source/Vector';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import TileGrid from 'ol/tilegrid/TileGrid';
import WMSServerType from 'ol/source/WMSServerType';
import clamp from 'lodash-es/clamp';

import { getLayerExtent } from 'cadenza/api-client/maps-api';
import { fitMapView } from 'cadenza/map/map-utils';
import { logger } from 'cadenza/utils/logging';
import { PhotoLayer } from 'cadenza/map/photo-layer/photo-layer';
import { ClassicSketchLayer } from 'cadenza/map/classic-sketch-layer/classic-sketch-layer';
import { assert } from 'cadenza/utils/custom-error';
import { cadenzaUrl, isExternal } from 'cadenza/utils/cadenza-url/cadenza-url';
import { LayerProperty } from 'cadenza/map/layer-property';
import {
  BackendAbortController
} from 'cadenza/api-client/abort-controller/backend-abort-controller';
import { SingleExecutionHelper } from 'cadenza/api-client/abort-controller/single-execution-helper';
import { loadImageAsObjectUrl } from 'cadenza/utils/load-image';
import { SourceProperty } from 'cadenza/map/source-property';
import { MapProperty } from 'cadenza/map/map-property';
import { createVectorLayer } from 'cadenza/map/vector-layer/vector-layer';
import { VectorLayerSource } from 'cadenza/map/vector-layer/vector-layer-source';
import { animateZoom } from 'cadenza/map/actions/zoom-by-delta';
import { ActionsName } from 'cadenza/map/layer-actions/layer-actions-name';
import { ViewProperty } from 'cadenza/map/view-property';
import { getSuggestedExtent } from 'cadenza/api-client/layer-extent-api';
import { OnDemandError } from 'cadenza/map/custom/workbook/on-demand-error';
import { context } from 'cadenza/map/context';
import { roundScale } from 'cadenza/map/round-scale';
import { cloneProjection } from 'cadenza/map/proj-utils';
import { FeatureName, isFeatureAvailable } from 'cadenza/features';
import { SketchLayer } from 'cadenza/workbook/sketch/sketch-layer/sketch-layer';
import { SketchLayerSource } from 'cadenza/workbook/sketch/sketch-layer/sketch-layer-source';

const DOTS_PER_INCH = 96;
const INCH_TO_METER = 0.0254;

export const WORKBOOK_VIEW_ON_DEMAND = {
  ON: 'on',
  OFF: 'off'
};

const MIN_SCALE_RANGE = 0;
const MAX_SCALE_RANGE = Number.MAX_VALUE;

export function isConfigurableWithCurrentMapScale (layer, map) {
  const constraints = layer.get(LayerProperty.CONFIGURATION_CONSTRAINTS);

  if (!constraints.configurable || constraints.supportedScaleRanges.length === 0) {
    return false;
  }
  const currentScale = roundScale(context(map).mapScale, 2, 0);
  const isUpperBoundSuperiorToCurrentScale = (scaleRange) => {
    return scaleRange.maxIncluded ? scaleRange.max >= currentScale : scaleRange.max > currentScale;
  };
  const isLowerBoundInferiorToCurrentScale = (scaleRange) => {
    return scaleRange.minIncluded ? scaleRange.min <= currentScale : scaleRange.min < currentScale;
  };

  const visibleLayersCount = constraints.supportedScaleRanges.reduce((count, scaleRange) => {
    if (scaleRange.min === MIN_SCALE_RANGE && scaleRange.max === MAX_SCALE_RANGE) {
      // no bounds set
      return count + 1;
    } else if (scaleRange.min === MIN_SCALE_RANGE) {
      // only upper bound set
      return isUpperBoundSuperiorToCurrentScale(scaleRange) ? count + 1 : count;
    } else if (scaleRange.max === MAX_SCALE_RANGE) {
      // only lower bound set
      return isLowerBoundInferiorToCurrentScale(scaleRange) ? count + 1 : count;
    } else {
      // upper and lower bounded
      return isLowerBoundInferiorToCurrentScale(scaleRange)
      && isUpperBoundSuperiorToCurrentScale(scaleRange) ? count + 1 : count;
    }
  }, 0);
  return visibleLayersCount === 1;
}

export function updateLayer (layer, props, reload = true) {
  let changed = false;
  const source = layer.getSource();
  if (source instanceof ImageWMSSource) {
    if (source.ratio_ !== props.source.ratio) {
      source.ratio_ = props.source.ratio;
      changed = true;
    }
  }

  if (source instanceof SketchLayerSource) {
    // SketchLayer in workbooks does not have workbook id and hash in the url, because of this, the reload flag is not relevant here.
    // If the url has changed, then assume we should reload. See the setUrl method, if the url is the same, we won't reload
    // For sketch we don't need to create a cadenzaUrl, we need an apiUrl (and after a cadenzaUrl) as done in the ajax util.
    // Below using cadenzaUrl to create a full url (including contextPath) works because the other layers request data through OL directly
    // (they don't use our ajax util)
    source.setUrl(props.source.url);
  } else {
    const newSourceUrl = cadenzaUrl(props.source.url);
    if (newSourceUrl && !isExternal(newSourceUrl)) {
      setLayerSourceUrlAndReloadIfRequired(layer, newSourceUrl, reload);
      changed ||= reload;
    }
  }

  if (!reload) {
    // If layer reload should be avoided, set the new legend url silently to not trigger legend image update.
    layer.set(LayerProperty.LEGEND_URL, props.legendUrl, true);
  }

  setMinMaxResolution(layer, props);

  const onchange = () => { changed = true; };
  layer.once('propertychange', onchange);
  layer.setProperties(getCommonProps(layer, props));
  layer.un('propertychange', onchange);
  if (changed) {
    layer.changed(); // The only listeners for this seems to result in PluggableMap::render
  }
}

function setLayerSourceUrlAndReloadIfRequired (layer, newSourceUrl, reload) {
  const source = layer.getSource();
  const oldSourceUrl = getSourceUrl(source);

  // If url has changed and reload flag is true, setting new url will trigger layer source data reloading.
  setSourceUrl(source, newSourceUrl, !reload);

  // If reload flag is true we should trigger layer data reloading even if the url has not changed.
  if (reload && oldSourceUrl === newSourceUrl) {
    refreshLayer(layer);
  }
}

export function createLayer (props = {}, map) {
  assert(props.type != null, 'Layer type is not defined');
  const factory = layerFactories[props.type];
  assert(factory != null, `There is no factory for layer type "${props.type}"`);
  const layer = factory(props, map);
  layer.setProperties(getCommonProps(layer, props));
  if (props.attributions) {
    layer.getSource().setAttributions(props.attributions);
  }
  setMinMaxResolution(layer, props);
  return layer;
}

export function scaleToResolution (scale) {
  return scale * INCH_TO_METER / DOTS_PER_INCH;
}

export function isCancelable (layer) {
  // currently only layer with wms source are cancelable. The cancellation of the requests is done
  // with SingleExecutionHelper that is set as a property on cancelable layers.
  return layer.get(LayerProperty.IMG_LOAD_EXECUTION_HELPER) ?? false;
}

function setMinMaxResolution (layer, props) {
  if (props.minScale) {
    let minScale = props.minScale.value;
    // If the min scale is non-inclusive, use a slightly bigger minimum, so that the server does not
    // consider the layer invisible and gives us a blank map image when we are at the lower limit of
    // the visible range.
    // The difference to the configured min scale must be big enough that it is not rounded down to
    // the min scale again when checking the layer visibility on the server (in ScaleRange#contains).
    if (props.minScale.endpointIncluded === false) {
      minScale = minScale + scaleLimitEpsilon(minScale);
    }
    layer.setMinResolution(scaleToResolution(minScale));
  } else {
    // if there is no minScale defined then set to default 0
    layer.setMinResolution(0);
  }
  if (props.maxScale) {
    let maxScale = props.maxScale.value;
    // For the same reason as the increase of the min scale, the max scale has to be lowered a bit.
    if (props.maxScale.endpointIncluded === false) {
      maxScale = maxScale - scaleLimitEpsilon(maxScale);
    }
    layer.setMaxResolution(scaleToResolution(maxScale));
  } else {
    // if there is no maxScale defined then set to default Infinity
    layer.setMaxResolution(Infinity);
  }
}

function getCommonProps (layer, {
  type,
  id,
  name,
  suggestedExtent,
  legendUrl,
  helpUrl,
  visible,
  visibleInLayerList,
  hasFeatures = false,
  hasFeatureInfo = false,
  featureInfoVersion,
  geometryType,
  hasAttachments = false,
  hasProcessings = false,
  selectionCount = 0,
  selectedFeatureIds = [],
  hasTimelines = false,
  axisRotated = false,
  configurationConstraints = { configurable: false, supportedScaleRanges: [] },
  filterable = false,
  filtered = false,
  exportableAsShapeFile = false,
  exportableAsKml = false,
  exportableAsExcel = false,
  intersectable = false,
  snappable = false,
  repositoryItemTheme = false,
  transientRepositoryItemTheme = false,
  versionTheme = false,
  privileges = [],
  actions = [],
  lifecycleActions = {},
  reloadInterval = 0,
  groupTheme = false,
  toggleGroupChoices,
  normalGroupChoices,
  selectorLayer = false,
  selectorExecutionType,
  conditionDefinitionSetId,
  conditionValuesSetHash,
  initialConditionValuesSetHash,
  attributes,
  zoomOnFilterChangeMode,
  hasApplicableTemplates,
  extendedInfo = [],
  structuredLegendLayer = false,
  supportsGeometryEdit = false,
  supportsAttributesEdit = false,
  supportsFeatureCreation = false,
  hasEmptyRequiredConditions = false,
  supportsFeatureRemoval = false,
  toggleGroupLayers = [],
  toggleGroupActiveLayer,
  toggleGroupActiveLayerIndex
}) {
  if (layer instanceof ClassicSketchLayer) {
    actions.unshift(ActionsName.EditSketch);
  }
  if (helpUrl) {
    actions.unshift(ActionsName.LayerHelp);
  }
  if (actions.includes(ActionsName.ChangeLayerTemplate) && (configurationConstraints.configurable || hasApplicableTemplates)) {
    actions.push(hasApplicableTemplates ? ActionsName.ShowLayerConfigurationMenu : ActionsName.ShowLayerConfigurationPanel);
  } else if (configurationConstraints.configurable) {
    actions.push(ActionsName.ShowLayerConfigurationPanel);
  }

  return {
    type,
    id,
    name,
    suggestedExtent,
    legendUrl,
    helpUrl,
    visible,
    visibleInLayerList,
    hasFeatures,
    hasAttachments,
    hasProcessings,
    selectionCount,
    selectedFeatureIds,
    hasTimelines,
    axisRotated,
    configurationConstraints,
    filterable,
    filtered,
    exportableAsShapeFile,
    exportableAsKml,
    exportableAsExcel,
    intersectable,
    snappable,
    repositoryItemTheme,
    transientRepositoryItemTheme,
    versionTheme,
    privileges,
    actions,
    lifecycleActions,
    reloadInterval,
    hasFeatureInfo,
    featureInfoVersion,
    geometryType,
    groupTheme,
    toggleGroupChoices,
    normalGroupChoices,
    selectorLayer,
    selectorExecutionType,
    conditionDefinitionSetId,
    conditionValuesSetHash,
    initialConditionValuesSetHash,
    attributes,
    zoomOnFilterChangeMode,
    hasApplicableTemplates,
    extendedInfo,
    structuredLegendLayer,
    supportsAttributesEdit,
    supportsGeometryEdit,
    supportsFeatureCreation,
    supportsFeatureRemoval,
    hasEmptyRequiredConditions,
    toggleGroupLayers,
    toggleGroupActiveLayer,
    toggleGroupActiveLayerIndex
  };
}

const layerFactories = {

  ArcGISImageLayer ({ source, axisRotated }, map) {
    const imageSource = createImageArcGISRestSource({
      ...source,
      projection: cloneProjection(map.getView().getProjection(), { axisOrientation: !axisRotated ? 'enu' : 'neu' })
    }, map.get(MapProperty.HIDPI));
    return new ImageLayer({
      source: imageSource
    });
  },

  ArcGISTileCacheLayer ({ source, axisRotated }, map) {
    const layerSource = createTileCacheArcGISRestSource({
      ...source,
      projection: cloneProjection(map.getView().getProjection(), { axisOrientation: !axisRotated ? 'enu' : 'neu' })
    }, map.get(MapProperty.HIDPI));
    return new TileLayer({
      source: layerSource
    });
  },

  WmsLayer ({ source, axisRotated }, map) {
    // some WMS servers will respond with an empty transparent image if the STYLES parameter is not
    // set at all, but work fine when the parameter has an empty value ("[...]&STYLES=&[...]");
    // same behavior as in Cadenza Desktop (see WmsRequestBuilder.java)
    if (source.style === undefined || source.style === null) {
      source.style = '';
    }
    const [ imageWmsSource, imgLoadExecutionHelper ] = createImageWMSSource({
      ...source,
      projection: cloneProjection(map.getView().getProjection(), { axisOrientation: !axisRotated ? 'enu' : 'neu' })
    },
    map.get(MapProperty.HIDPI));
    const layer = new ImageLayer({
      source: imageWmsSource
    });
    cancelImageLoadOnLayerRemove(map, layer, imgLoadExecutionHelper);
    layer.set(LayerProperty.IMG_LOAD_EXECUTION_HELPER, imgLoadExecutionHelper);
    layer.set(LayerProperty.AVAILABLE_STYLES, source.availableStyles);
    return layer;
  },

  WmtsLayer ({
    source: { url, version, format, layer: layerName, style, matrixSet, tileGrid },
    id
  }) {
    const src = new WMTSSource({
      url: cadenzaUrl(url),
      format,
      version,
      layer: layerName,
      style,
      matrixSet,
      tileGrid: new WMTSTileGrid(tileGrid),
      // The tile pixel ratio is supposed to be set when the WMTS service has high-resolution tiles
      // (most commonly 512x512 instead of 256x256), but on high-res screens a larger tile pixel ratio
      // also improves the rendering of low-res WMTS tiles (and without a significantly higher memory
      // footprint in the browser), probably because most of the time we use a zoom level between the
      // ones that the tiles were optimized for, so that they have to be resized.
      // Thus we set the tile pixel ratio to the device pixel ratio, independent of the WMTS service's
      // tile size.
      tilePixelRatio: window.devicePixelRatio
    });
    return new TileLayer({
      source: src
    });
  },

  PhotoLayer ({ masterLayer }, map) {
    return new PhotoLayer({
      masterLayerId: masterLayer,
      map
    });
  },

  ClassicSketchLayer ({ id, source }, map) {
    const [ imageWmsSource, imgLoadExecutionHelper ] = createImageWMSSource({
      ...source,
      ratio: 1.0
    }
    );
    const layer = new ClassicSketchLayer({
      source: imageWmsSource,
      layerId: id,
      map
    });
    cancelImageLoadOnLayerRemove(map, layer, imgLoadExecutionHelper);
    return layer;
  },

  SketchLayer ({ source }, map) {
    return new SketchLayer(map, source.url);
  },

  VectorLayer ({
    id,
    loadingStrategy,
    opacity,
    vectorImageLayer,
    liveLayer,
    source: {
      url,
      projection
    },
    areTimelinePointsConnectedWithLines,
    timelineTimestampAttributeName,
    labelAttributeNames
  }, map) {
    return createVectorLayer(map, id, {
      url: cadenzaUrl(url),
      loadingStrategy,
      opacity,
      vectorImageLayer,
      liveLayer,
      projection,
      areTimelinePointsConnectedWithLines,
      timelineTimestampAttributeName,
      labelAttributeNames
    });
  }

};

function cancelImageLoadOnLayerRemove (map, layer, imgLoadExecutionHelper) {
  map.getLayers().on('remove', ({ element: removedLayer }) => {
    if (removedLayer.get(LayerProperty.ID) === layer.get(LayerProperty.ID)) {
      imgLoadExecutionHelper.cancelCurrent();
    }
  });
}

function createImageWMSSource (source, hidpi = false) {
  const backendCancelable = !source.displayAsDirectAccessWmsTheme;
  const singleExecutionHelper = new SingleExecutionHelper();
  const params = source.params || {};

  const sourceOptions = {
    url: cadenzaUrl(source.url),
    ratio: source.ratio,
    params: {
      VERSION: source.version,
      FORMAT: source.format,
      LAYERS: source.layers,
      STYLES: source.style,
      EXCEPTIONS: source.exceptions,
      ...params
    },
    projection: source.projection,
    crossOrigin: isFeatureAvailable(FeatureName.WEBAPP_INTEGRATION) && isExternal(source.url) ? 'anonymous' : null
  };

  if (hidpi) {
    // The source option "hidpi" is already enabled by default, but high-DPI rendering is only enabled
    // when a server type is specified (see ImageWMS.getImageInternal()).
    // The only thing influenced by the server type is the format of the parameter to specify the
    // resolution for high-res devices.
    // With WMSServerType.QGIS OpenLayers adds a parameter called "DPI" to the get map request on
    // devices with devicePixelFactor > 1, which also works for Cadenza.
    sourceOptions.serverType = WMSServerType.QGIS;
  }

  const imageWMSSource = new ImageWMSSource(sourceOptions);

  // Add cancelId and _ parameters only to the internal non-locator (non-overview-map) layer urls, because
  // 1. Some external WMS servers throw exception if unknown parameter is specified.
  // 2. We want to cache overview map layer request result images to make server load less,
  // it is highly unlikely that there are layers with dynamically changing content in the overview map.
  if (!isExternal(source.url) && !params.locator) {
    const imageLoadFunction = createImageLoadFunction(imageWMSSource, backendCancelable, singleExecutionHelper);
    imageWMSSource.setImageLoadFunction(imageLoadFunction);
  }

  return [ imageWMSSource, singleExecutionHelper ];
}

function createImageArcGISRestSource (source, hidpi = false) {
  const params = source.params || {};

  // We only support external ArcGISRest URLs, because of this we cannot cancel the request on the backend.
  // How about cancelling the request on the frontend using AbortController in order not to execute the imageLoad/Error listeners?
  // This is not necessary since OL already takes care of it in ol.source.ImageWrapper#load()
  const sourceOptions = {
    url: cadenzaUrl(source.url),
    ratio: source.ratio,
    params: {
      VERSION: source.version,
      FORMAT: source.format,
      LAYERS: `show:${source.layers}`,
      ...params
    },
    projection: source.projection
  };

  return new ImageArcGISRestSource(sourceOptions);
}

function createTileCacheArcGISRestSource (source, hidpi = false) {
  const params = source.params || {};

  // We only support external ArcGISRest URLs, because of this we cannot cancel the request on the backend.
  // How about cancelling the request on the frontend using AbortController in order not to execute the imageLoad/Error listeners?
  // This is not necessary since OL already takes care of it in ol.source.ImageWrapper#load()
  const sourceOptions = {
    url: cadenzaUrl(source.url),
    ratio: source.ratio,
    params: {
      VERSION: source.version,
      FORMAT: source.format,
      ...params
    },
    tileGrid: new TileGrid(source.tileGrid),
    projection: source.projection
  };

  return new XYZSource(sourceOptions);
}

/**
 * Inspired by ol/source/Image::defaultImageLoadFunction, but:
 * 1. avoid cache in IE11 dss
 * 2. cancel outdated image requests
 * 3. gets additional custom messages from the response header 'X-Disy-Messages' and sets them to SourceProperty.MESSAGES
 * 4. Does not issue a request if reload is false. (Prevent reloading images for unchanged data views.)
 *
 * If the source has onDemand setting ON then don't execute the image loading request.
 *
 * @param {object} source - (ol.ImageWMS)
 * @param {boolean} backendCancelable
 * @param {SingleExecutionHelper} singleExecutionHelper
 * @return {function(object, string): void} - A function that loads an image from a WMS source or skips loading if reload flag ist set to false.
 */
function createImageLoadFunction (source, backendCancelable, singleExecutionHelper) {
  return function loadImageFn (image, src) {
    const img = image.getImage();
    let abortController;
    if (backendCancelable) {
      abortController = new BackendAbortController();
      abortController.abortOnUnload = true;
    } else {
      abortController = new AbortController();
    }

    if (loadImageFn.cancelId) {
      abortController.signal.id = loadImageFn.cancelId;
    }

    let imageLoadingFinished = false;
    const loadImagePromise = source.get(SourceProperty.WORKBOOK_VIEW_ON_DEMAND) === WORKBOOK_VIEW_ON_DEMAND.ON
      ? Promise.reject(new OnDemandError())
      : loadImageAsObjectUrl(src, abortController.signal);

    loadImagePromise
      .then(({ objectUrl, messages }) => {
        imageLoadingFinished = true;
        img.onload = () => {
          URL.revokeObjectURL(objectUrl); // revoke object URL when image is loaded to free memory
        };
        img.src = objectUrl;
        source.set(SourceProperty.MESSAGES, messages);
      })
      .catch(error => {
        imageLoadingFinished = true;
        if (error.name !== 'AbortError' && !(error instanceof OnDemandError)) {
          logger.error('Failed to load map image.', error);
        }
        // trigger error on the img element, so OpenLayers gets an event, that image wasn't loaded.
        img.dispatchEvent(new CustomEvent('error'));
      });

    singleExecutionHelper.submit(abortController, () => imageLoadingFinished);
  };
}

/**
 * Calls changed() function for the layer source, which triggers the "changed" event on the layer source.
 * Loading strategy, if it is set, can then decide to reload the data.
 * If given layer is a vector layer and its source is an instance of VectorLayerSource,
 * the clearAndRefreshVectorLayer function is called instead to clear the source and trigger the vector layer data reload.
 *
 * @param layer - layer to refresh
 */
export function refreshLayer (layer) {
  if (layer) {
    const layerSource = layer.getSource();
    if (layerSource instanceof VectorLayerSource) {
      clearAndRefreshVectorLayer(layer);
    } else if (layerSource) {
      layerSource.changed();
    }
  }
}

/**
 * Reloads vector layer features.
 * Calls refresh function of the layer source, which clears the source and reloads the features from the server.
 *
 * @param layer - vector layer to refresh
 */
export function clearAndRefreshVectorLayer (layer) {
  if (layer && layer.getSource) {
    const layerSource = layer.getSource();
    if (layerSource instanceof VectorSource) {
      layerSource.refresh();
    }
  }
}

/**
 * Check if a layer can be opened in dashboard mode
 *
 * @param layer - to check if it has a table attribute
 * @return true if the layer can be opened in dashboard mode, i.e. with the table by its side
 */
export function hasAttributeTable (layer) {
  return layer.get(LayerProperty.ATTRIBUTES);
}

/**
 * Creates the feature selection data map with the corresponding format
 *
 * @param layerIds - represents the layer to which the selection applies
 * @param selectedFeatureIds - the selection itself
 * @return the selectionMap in the correct format
 */
export function createFeatureSelectionData (layerIds, selectedFeatureIds) {
  const selectionMap = {};
  layerIds.forEach(layerId => { selectionMap[layerId] = selectedFeatureIds; });
  return selectionMap;
}

const ZoomOnFilterChangeMode = {
  DO_NOTHING: 'DO_NOTHING',
  FIT_RESULT: 'FIT_RESULT',
  FIT_RESULT_IF_VIEW_EMPTY: 'FIT_RESULT_IF_VIEW_EMPTY'
};

export function zoomToResult (map, layer) {
  const zoomOnFilterChangeMode = layer.get(LayerProperty.ZOOM_ON_FILTERING_MODE);
  if (zoomOnFilterChangeMode !== ZoomOnFilterChangeMode.DO_NOTHING) {
    const view = map.getView();
    const srsCode = view.getProjection().getCode();
    const layerId = layer.get(LayerProperty.ID);
    getLayerExtent(layerId, srsCode).then(extent => {
      const currentExtent = map.getView().calculateVisibleExtent();
      if (extent && (zoomOnFilterChangeMode === ZoomOnFilterChangeMode.FIT_RESULT
        || (zoomOnFilterChangeMode === ZoomOnFilterChangeMode.FIT_RESULT_IF_VIEW_EMPTY && !intersects(currentExtent, extent)))) {
        fitMapView(map, extent);
      }
    }).catch((error) => logger.error('Could not zoom to result envelope', error));
  }
}

export function zoomToVisible (map, layer) {
  const view = map.getView();
  const resolution = view.getResolution();
  const { minResolution, maxResolution } = layer.getProperties();
  const epsilon = 0.001;
  const targetResolution = clamp(
    resolution,
    minResolution * (1 + epsilon),
    maxResolution * (1 - epsilon));
  const targetZoom = view.getZoomForResolution(targetResolution);
  animateZoom(view, targetZoom, 250);
}

/**
 * Adapted to the algoritm used in the Java method ScaleRangeUtilities#getRoundedScale} (used by
 * ScaleRange#contains).<br>
 * When checking if a layer is in the visible range, ScaleRangeUtilities#getRoundedScale is used to
 * round the scaleLimits to "2.5 digits" (cuts off everything but the first 3 digits and rounds the
 * 3rd one to either 0 or 5).<br>
 * This method calculates an epsilon that has to be added to (in case of min) or subtracted from
 * (in case of max) a ScaleRange limit to get the a number that is close to the limit, but does not
 * get rounded to it when checking if the layer is visible.<br>
 *
 * If the algorithm of ScaleRangeUtilities#getRoundedScale changes, this function might need to change,
 * too.<br>
 * Should be kept in sync with its Java counterpart ZoomAlgorithm#limitEpsilon.
 *
 * @param {number} scaleLimit - max or min of scale range
 * @return {number} number that has to be added to the min or subtracted from the max
 */
function scaleLimitEpsilon (scaleLimit) {
  return scaleLimit * 0.05 * (1.0 / firstDigit(scaleLimit));
}

function firstDigit (number) {
  return parseFloat((number.toString())[0]);
}

/**
 * Sets the selected features to empty and refreshes the layer
 *
 * @param layer - The layer which we want to reset the selection on
 */
export function resetSelection (layer) {
  layer.set(LayerProperty.SELECTED_FEATURE_IDS, []);
  refreshLayer(layer);
}

export function getSourceUrl (source) {
  if (source instanceof VectorSource) {
    return source.getUrl();
  } else if (source instanceof ImageWMSSource) {
    return source.getUrl();
  } else if (source instanceof WMTSSource) {
    const urls = source.urls;
    assert(urls.length === 1, 'WMTS source should have exactly one url');
    return urls[0];
  } else {
    throw new Error('Unknown source type, unable to get url');
  }
}

/**
 * Updates the url of the OL layer source.
 *
 * @param {ol.Source} source - OL source
 * @param {string} url - New layer source data url
 * @param {boolean} [silent = false] - If true, new url will be set silently and the layer source data will not be reloaded
 */
function setSourceUrl (source, url, silent = false) {
  const oldSourceUrl = getSourceUrl(source);
  if (oldSourceUrl === url) {
    return;
  }
  if (source instanceof VectorLayerSource) {
    source.setUrl(url, silent);
  } else if (source instanceof ImageWMSSource) {
    if (silent) {
      source.url_ = url;
    } else {
      source.setUrl(url);
    }
  } else if (source instanceof WMTSSource) {
    if (silent) {
      source.urls = [ url ];
      source.tileUrlFunction = source.createFromWMTSTemplate(url);
    } else {
      source.setUrls([ url ]);
    }
  } else {
    throw new Error('Unknown source type, unable to set url');
  }
}

export function getAndUpdateLayerSuggestedExtent (layer, map) {
  const view = map.getView();
  const srsCode = view.getProjection().getCode();
  return getSuggestedExtent(layer.get(LayerProperty.ID), srsCode)
    .then(extent => {
      if (extent) {
        layer.set(LayerProperty.SUGGESTED_EXTENT, extent);
        return extent;
      }
      return view.get(ViewProperty.MAX_EXTENT);
    })
    .catch(error => {
      logger.error('Error retrieving layer extent.', error);
      return view.get(ViewProperty.MAX_EXTENT);
    });
}

export function isLayerWorkbookDataViewLayer (layer) {
  const layerSource = layer.getSource();
  return (layerSource instanceof ImageWMSSource || layerSource instanceof VectorLayerSource)
    && layerSource.getUrl().includes('/api/workbooks/');
}
